// @givmo/board — a member's two facts, said once each (CBG-YR3-ROLES layer 1, D-CBG-21).
//
// The first card on the member page, a property list: a quiet label column and each value beside it.
//
//   * DIRECTOR — Yes or No, and what that means, since it is the one fact that decides votes, quorum
//     and written consents. The meaning is said only of someone serving: a former member's seat
//     counts toward no quorum and is on no new consent's roster, whatever the fact was.
//   * TITLES — each title the person holds, the title alone. Where a title came from (carried over
//     from the old chair or secretary seat, or given here) is the seat's history, not a note under it.
//     Layer 2 (`l2-member-dates`) adds the board's OWN dates beside each title ("Since Oct 2, 2024")
//     and a row of EARLIER TITLES, each with its dates or a plain "No dates recorded".
//     Layer 3 (`l3-member-duties`) adds, under each title held, what that title does in Givmo under the
//     board's ADOPTED duties, with where each is written — nothing before the board adopts them.
//     CBG-YR3-ROLES-HOLDOVER-PAGE: a title the person still holds past its recorded term end (the
//     holdover, backend `board-officer-roles.md` §15) says so, as the Officers page does: "Since Oct 2,
//     2024 · Term ended Jun 30, 2026 – still serving" (`memberTitleDatesLine`).
//
// The same-access sentence closes the card, because it answers the question a "No" raises: someone who
// isn't a director still gets the packets, the records and the invitations.

import { Card, CardBody } from '@givmo/design-system';
import type { ReactNode } from 'react';
import { todayAsDateInput } from '../../lib/display';
import {
  currentTitles,
  endedTitles,
  isDirector,
  memberTitleDatesLine,
  titleDatesLine,
  type SeatFacts,
} from './officer-roles';
import { titleDutiesBySource, type OfficerDuties } from './officer-duties';
import { officerRolesL2Words as l2 } from './officer-roles-l2-words';
import { officerRolesL3Words as l3 } from './officer-roles-l3-words';
import { officerRolesWords as w } from './officer-roles-words';

const VALUE = 'font-sans text-s14 font-w500 tracking-givmo text-text-strong';
const SECONDARY = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
/**
 * A title and its dates: the dates under the title on a phone, beside it from `sm` up, so every row
 * of the card lays out alike whatever its dates' length (a long held-over line would otherwise wrap
 * under its title while a short one sat beside the next).
 */
const TITLE_ROW = 'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-8';

/** A property row: the label in a quiet column and the value beside it; stacked on a phone. */
function FactRow({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div
      className="grid grid-cols-1 gap-4 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-16"
      data-testid={testId}
    >
      <span className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary">
        {label}
      </span>
      <div className="flex min-w-0 flex-col gap-6">{children}</div>
    </div>
  );
}

/** What one title does in Givmo, under the title: its duties by source, a quiet rule beside them. */
function TitleDuties({ duties, titleId }: { duties: OfficerDuties | null; titleId: string }) {
  const groups = duties === null ? [] : titleDutiesBySource(duties, titleId);
  if (groups.length === 0) return null;
  return (
    <div
      className="mt-2 flex flex-col gap-4 border-l-2 border-border-strong pl-12"
      data-testid={`member-detail-title-duties-${titleId}`}
    >
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        {l3.whatItDoes}
      </span>
      {groups.map((group) => (
        <div key={group.source} className="flex flex-col gap-2">
          <span className={`${SECONDARY} wrap-anywhere`}>{group.duties.join(' · ')}</span>
          <span className={`${QUIET} wrap-anywhere`}>{group.source}</span>
        </div>
      ))}
    </div>
  );
}

export function DirectorAndTitlesCard({
  seat,
  duties = null,
}: {
  seat: SeatFacts & { status?: string | null };
  /** The board's duties read (layer 3), or null while it loads or where it isn't read. */
  duties?: OfficerDuties | null;
}) {
  const director = isDirector(seat);
  const titles = currentTitles(seat);
  const earlier = endedTitles(seat);
  const serving = (seat.status ?? '') === 'active';
  const today = todayAsDateInput();

  return (
    <Card testId="member-detail-roles">
      <CardBody className="flex flex-col gap-12">
        <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">{w.card}</h2>

        <div className="flex flex-col gap-12">
          <FactRow label={w.director} testId="member-detail-director">
            <span className={VALUE}>{director ? w.yes : w.no}</span>
            {serving ? (
              <span className={SECONDARY}>{director ? w.isDirectorMeans : w.notDirectorMeans}</span>
            ) : null}
          </FactRow>

          <FactRow label={w.titles} testId="member-detail-titles">
            {titles.length === 0 ? (
              <span className={SECONDARY}>{w.noTitles}</span>
            ) : (
              titles.map((title) => {
                const dates = memberTitleDatesLine(title, today);
                return (
                  <div key={title.id} className="flex flex-col gap-4">
                    <span className={TITLE_ROW}>
                      <span className={`${VALUE} wrap-anywhere`}>{title.label}</span>
                      {dates !== null ? (
                        <span
                          className={QUIET}
                          data-testid={`member-detail-title-dates-${title.id}`}
                        >
                          {dates}
                        </span>
                      ) : null}
                    </span>
                    <TitleDuties duties={duties} titleId={title.title_id} />
                  </div>
                );
              })
            )}
          </FactRow>

          {earlier.length > 0 ? (
            <FactRow label={l2.earlierTitles} testId="member-detail-earlier-titles">
              {earlier.map((title) => (
                <span key={title.id} className={TITLE_ROW}>
                  <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary wrap-anywhere">
                    {title.label}
                  </span>
                  <span className={QUIET}>{titleDatesLine(title) ?? l2.heldNoDates}</span>
                </span>
              ))}
            </FactRow>
          ) : null}
        </div>

        <p className={`${QUIET} border-t border-border-subtle pt-12`}>{w.sameAccess}</p>
      </CardBody>
    </Card>
  );
}
